import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Stage, Layer, Image as KImage, Line, Rect, Circle, Arrow, Text, Label, Tag, Group } from "react-konva";
import api, { API, getToken } from "../lib/api";
import jsAruco from "js-aruco2";
import { toast } from "sonner";
import {
  ArrowLeft, MousePointer2, Ruler, Crosshair, ArrowUpRight, Square, Circle as CircleIcon,
  PenTool, Type, Trash2, Save, ImagePlus, Printer, Mic, Loader2, Sparkles, Paperclip,
  Wand2, Scissors, FileText,
} from "lucide-react";

const MAXW = 880;
const ARUCO_CM = 15;
const COLORS = ["#0f172a", "#ef4444", "#0284c7", "#16a34a", "#f59e0b"];
const TOOLS = [
  { key: "select", label: "Sélection", icon: MousePointer2 },
  { key: "calibrate", label: "Calibrer l'échelle", icon: Crosshair },
  { key: "dimension", label: "Cote", icon: Ruler },
  { key: "arrow", label: "Flèche", icon: ArrowUpRight },
  { key: "rect", label: "Rectangle", icon: Square },
  { key: "circle", label: "Cercle", icon: CircleIcon },
  { key: "pen", label: "Main levée", icon: PenTool },
  { key: "text", label: "Texte", icon: Type },
];

const uid = () => Math.random().toString(36).slice(2, 9);
const dist = (a, b, c, d) => Math.hypot(c - a, d - b);

function solveHomography(src, dst) {
  const A = [], b = [];
  for (let i = 0; i < 4; i++) {
    const { x, y } = src[i]; const u = dst[i].x, v = dst[i].y;
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v);
  }
  for (let col = 0; col < 8; col++) {
    let piv = col;
    for (let r = col + 1; r < 8; r++) if (Math.abs(A[r][col]) > Math.abs(A[piv][col])) piv = r;
    [A[col], A[piv]] = [A[piv], A[col]]; [b[col], b[piv]] = [b[piv], b[col]];
    const d = A[col][col]; if (Math.abs(d) < 1e-10) return null;
    for (let r = 0; r < 8; r++) {
      if (r === col) continue;
      const f = A[r][col] / d;
      for (let k = col; k < 8; k++) A[r][k] -= f * A[col][k];
      b[r] -= f * b[col];
    }
  }
  const h = []; for (let i = 0; i < 8; i++) h.push(b[i] / A[i][i]);
  h.push(1); return h;
}
function applyH(h, x, y) {
  const w = h[6] * x + h[7] * y + h[8];
  return [(h[0] * x + h[1] * y + h[2]) / w, (h[3] * x + h[4] * y + h[5]) / w];
}
function measureCm(cal, x1, y1, x2, y2) {
  if (cal && cal.homography) {
    const p1 = applyH(cal.homography, x1, y1), p2 = applyH(cal.homography, x2, y2);
    return Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
  }
  if (cal && cal.pxPerCm) return Math.hypot(x2 - x1, y2 - y1) / cal.pxPerCm;
  return null;
}
const srcWithAuth = (url) => (url && url.includes("/api/files/")) ? `${url}${url.includes("?") ? "&" : "?"}auth=${getToken()}` : url;

export default function EtudeEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const stageRef = useRef(null);
  const fileRef = useRef(null);
  const [study, setStudy] = useState(null);
  const [active, setActive] = useState(0);
  const [tool, setTool] = useState("select");
  const [color, setColor] = useState("#ef4444");
  const [img, setImg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const [genBusy, setGenBusy] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const recRef = useRef(null);
  const loadedRef = useRef(false);

  const photo = study?.photos?.[active];
  const shapes = photo?.annotations || [];
  const calibration = photo?.calibration || null;
  const markerCm = study?.marker_cm || 15;
  const unit = study?.unit || "cm";
  const fmtNum = (cm) => cm == null ? "?" : (unit === "mm" ? Math.round(cm * 10) : cm.toFixed(1));
  const fmtLen = (cm) => cm == null ? "?" : `${fmtNum(cm)} ${unit}`;
  const measures = [];
  (study?.photos || []).forEach((p, pi) => {
    const cal = p.calibration;
    (p.annotations || []).forEach((a) => {
      if (a.type === "rect") {
        const w = measureCm(cal, a.x1, a.y1, a.x2, a.y1), h = measureCm(cal, a.x1, a.y1, a.x1, a.y2);
        if (w != null && h != null) measures.push({ photo: pi + 1, type: "Rectangle", val: `${fmtNum(w)} × ${fmtNum(h)} ${unit}` });
      } else if (a.type === "dimension") {
        const L = a.realCm != null ? a.realCm : measureCm(cal, a.x1, a.y1, a.x2, a.y2);
        if (L != null) measures.push({ photo: pi + 1, type: "Cote", val: fmtLen(L) });
      }
    });
  });

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    api.get(`/studies/${id}`).then((r) => setStudy(r.data)).catch(() => { toast.error("Étude introuvable"); navigate("/etudes"); });
  }, [id, navigate]);

  useEffect(() => { api.get(`/studies/${id}/documents`).then((r) => setQuotes(r.data)).catch(() => {}); }, [id]);

  useEffect(() => {
    if (!photo?.url) { setImg(null); return; }
    const im = new window.Image();
    im.crossOrigin = "anonymous";
    im.onload = () => setImg(im);
    im.src = srcWithAuth(photo.url);
  }, [active, photo?.url]);

  const displayW = img ? Math.min(img.naturalWidth, MAXW) : MAXW;
  const scale = img ? displayW / img.naturalWidth : 1;
  const displayH = img ? img.naturalHeight * scale : 480;

  const patch = useCallback((updater) => {
    setStudy((prev) => {
      const photos = prev.photos.map((p, i) => (i === active ? updater(p) : p));
      return { ...prev, photos };
    });
  }, [active]);

  const setShapes = (fn) => patch((p) => ({ ...p, annotations: fn(p.annotations || []) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/studies/${id}`, {
        title: study.title, category: study.category, brand: study.brand, model: study.model,
        client_id: study.client_id, client_name: study.client_name, document_id: study.document_id,
        marker_cm: study.marker_cm || 15,
        photos: study.photos,
      });
      setStudy((prev) => ({ ...prev, ...data }));
      toast.success("Étude enregistrée");
    } catch { toast.error("Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const addPhoto = async (files) => {
    const file = files && files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${API}${data.url.slice(4)}`;
      let newIndex = 0;
      setStudy((prev) => {
        const photos = [...(prev.photos || []), { path: data.path, url, annotations: [], calibration: null }];
        newIndex = photos.length - 1;
        return { ...prev, photos };
      });
      setActive(newIndex);
      toast.success("Photo ajoutée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'upload"); }
    finally { setUploading(false); }
  };

  const pos = () => {
    const p = stageRef.current.getPointerPosition();
    return { x: p.x, y: p.y };
  };

  const onDown = () => {
    if (!img) return;
    const p = pos();
    if (tool === "select") return;
    if (tool === "text") {
      const t = window.prompt("Texte de l'annotation :");
      if (t) setShapes((s) => [...s, { id: uid(), type: "text", x: p.x, y: p.y, text: t, color }]);
      return;
    }
    if (tool === "pen") { setDraft({ id: uid(), type: "pen", points: [p.x, p.y], color }); return; }
    setDraft({ id: uid(), type: tool, x1: p.x, y1: p.y, x2: p.x, y2: p.y, color });
  };
  const onMove = () => {
    if (!draft) return;
    const p = pos();
    if (draft.type === "pen") setDraft({ ...draft, points: [...draft.points, p.x, p.y] });
    else setDraft({ ...draft, x2: p.x, y2: p.y });
  };
  const onUp = () => {
    if (!draft) return;
    const d = draft; setDraft(null);
    if (d.type === "pen") { if (d.points.length > 3) setShapes((s) => [...s, d]); return; }
    if (dist(d.x1, d.y1, d.x2, d.y2) < 4 && d.type !== "text") return;
    if (d.type === "calibrate") {
      const cm = parseFloat(window.prompt("Longueur réelle de ce segment (en cm) ?", "20"));
      if (cm && cm > 0) {
        const pxPerCm = dist(d.x1, d.y1, d.x2, d.y2) / cm;
        patch((pp) => ({ ...pp, calibration: { pxPerCm } }));
        toast.success(`Échelle : 1 cm = ${pxPerCm.toFixed(1)} px`);
      }
      return;
    }
    if (d.type === "dimension") {
      const raw = window.prompt(`Valeur réelle de cette cote (en ${unit}) ?`, "");
      const v = parseFloat((raw || "").replace(",", "."));
      if (!v || v <= 0) return;
      d.realCm = unit === "mm" ? v / 10 : v;
    }
    setShapes((s) => [...s, d]);
  };

  const deleteSelected = () => { if (!selectedId) return; setShapes((s) => s.filter((x) => x.id !== selectedId)); setSelectedId(null); };
  useEffect(() => {
    const h = (e) => { if ((e.key === "Delete" || e.key === "Backspace") && selectedId) { e.preventDefault(); deleteSelected(); } };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, [selectedId]);

  const measureLabel = (s) => {
    const cm = s.realCm != null ? s.realCm : measureCm(calibration, s.x1, s.y1, s.x2, s.y2);
    return fmtLen(cm);
  };

  const genQuote = async () => {
    await save();
    setGenBusy(true);
    try {
      const { data } = await api.post(`/studies/${id}/generate-quote`);
      toast.success("Devis IA généré");
      navigate(`/documents/${data.document_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la génération IA"); }
    finally { setGenBusy(false); }
  };
  const toNesting = () => {
    const pieces = [];
    (study.photos || []).forEach((p) => {
      const cal = p.calibration;
      if (!cal) return;
      (p.annotations || []).forEach((a) => {
        if (a.type === "rect") {
          const wcm = measureCm(cal, a.x1, a.y1, a.x2, a.y1);
          const hcm = measureCm(cal, a.x1, a.y1, a.x1, a.y2);
          const w = Math.round(wcm || 0), h = Math.round(hcm || 0);
          if (w > 0 && h > 0) pieces.push({ name: `Pièce ${pieces.length + 1}`, width: w, height: h, qty: 1 });
        }
      });
    });
    if (!pieces.length) return toast.error("Tracez des rectangles et calibrez l'échelle pour envoyer des pièces à l'imposition.");
    navigate("/imposition", { state: { pieces } });
  };
  const autoCalibrate = () => {
    if (!img) return toast.error("Importez d'abord une photo");
    try {
      const cv = document.createElement("canvas");
      cv.width = img.naturalWidth; cv.height = img.naturalHeight;
      const ctx = cv.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, cv.width, cv.height);
      const AR = jsAruco.AR || jsAruco;
      const detector = new AR.Detector({ dictionaryName: "ARUCO" });
      const markers = detector.detect(imageData);
      if (!markers.length) return toast.error("Marqueur non détecté — vérifiez qu'il est bien visible et à plat.");
      let sum = 0, big = markers[0], bigA = 0;
      markers.forEach((m) => {
        const c = m.corners;
        const side = (dist(c[0].x, c[0].y, c[1].x, c[1].y) + dist(c[1].x, c[1].y, c[2].x, c[2].y) + dist(c[2].x, c[2].y, c[3].x, c[3].y) + dist(c[3].x, c[3].y, c[0].x, c[0].y)) / 4;
        sum += side;
        const area = Math.abs((c[0].x * (c[1].y - c[3].y) + c[1].x * (c[2].y - c[0].y) + c[2].x * (c[3].y - c[1].y) + c[3].x * (c[0].y - c[2].y)) / 2);
        if (area > bigA) { bigA = area; big = m; }
      });
      const pxPerCm = ((sum / markers.length) / markerCm) * scale;
      const srcPts = big.corners.map((p) => ({ x: p.x * scale, y: p.y * scale }));
      const dstPts = [{ x: 0, y: 0 }, { x: markerCm, y: 0 }, { x: markerCm, y: markerCm }, { x: 0, y: markerCm }];
      const H = solveHomography(srcPts, dstPts);
      patch((pp) => ({ ...pp, calibration: { pxPerCm, homography: H || null, markers: markers.length } }));
      toast.success(`${markers.length} marqueur(s) détecté(s) — échelle ${H ? "+ perspective " : ""}calée automatiquement.`);
    } catch (e) { toast.error("Détection impossible sur cette image"); }
  };

  const printMarker = () => {
    let svg = "";
    try { svg = new (jsAruco.AR || jsAruco).Dictionary("ARUCO").generateSVG(0); } catch (e) {}
    const w = window.open("", "_blank");
    w.document.write(`<!doctype html><html><head><title>Marqueur ArUco Fabriko</title>
      <style>@page{size:A4;margin:1.5cm} body{font-family:Arial;color:#0f172a;text-align:center}
      .box{width:${markerCm}cm;height:${markerCm}cm;margin:0.5cm auto} .box svg{width:${markerCm}cm !important;height:${markerCm}cm !important}
      h1{font-size:18px}p{font-size:12px;color:#475569;max-width:16cm;margin:8px auto}</style></head>
      <body><h1>Marqueur d'échelle Fabriko — ArUco ${markerCm} cm</h1>
      <div class="box">${svg}</div>
      <p>Imprimez à l'échelle 100% (décochez « ajuster à la page »). Vérifiez au réglet qu'un côté du carré noir mesure bien ${markerCm} cm.
      Collez le marqueur bien à plat sur la surface, prenez la photo de face, puis dans Fabriko cliquez « Auto » : l'échelle se cale automatiquement.</p></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 400);
  };

  // Voice note
  const toggleRecord = async () => {
    if (recording) { recRef.current?.stop(); setRecording(false); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => chunks.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: "audio/webm" });
        setVoiceBusy(true);
        try {
          const fd = new FormData(); fd.append("file", blob, "note.webm");
          const { data } = await api.post(`/studies/${id}/voice`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          setStudy((prev) => ({ ...prev, ...data }));
          toast.success("Note vocale transcrite et résumée");
        } catch { toast.error("Échec du traitement de la note vocale"); }
        finally { setVoiceBusy(false); }
      };
      recRef.current = mr; mr.start(); setRecording(true);
    } catch { toast.error("Micro non autorisé"); }
  };

  const openAttach = async () => {
    try { const { data } = await api.get("/documents"); setDocs(data.filter((d) => d.type === "devis")); }
    catch { setDocs([]); }
    setAttachOpen(true);
  };
  const attachToDoc = async (doc) => {
    try {
      const dataUrl = stageRef.current.toDataURL({ pixelRatio: 2 });
      const blob = await (await fetch(dataUrl)).blob();
      const fd = new FormData(); fd.append("file", new File([blob], "plan.png", { type: "image/png" }));
      const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${API}${up.data.url.slice(4)}`;
      const full = await api.get(`/documents/${doc.id}`);
      const d = full.data;
      const annexes = [...(d.annexes || []), { url, caption: `Plan — ${study.title}` }];
      await api.put(`/documents/${doc.id}`, { ...d, annexes });
      toast.success(`Plan ajouté aux annexes du devis ${d.number || ""}`);
      setAttachOpen(false);
    } catch { toast.error("Échec de l'ajout au devis"); }
  };

  if (!study) return <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  const strokeFor = (s) => (selectedId === s.id ? 4 : 2.5);
  const selectHandler = (sid) => () => { if (tool === "select") setSelectedId(sid); };

  return (
    <div className="space-y-4 animate-fade-up" data-testid="etude-editor">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate("/etudes")} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="etude-back"><ArrowLeft className="w-5 h-5" /></button>
        <input value={study.title} onChange={(e) => setStudy({ ...study, title: e.target.value })} data-testid="etude-title"
          className="text-xl font-display font-bold text-navy bg-transparent border-b border-transparent hover:border-slate-200 focus:border-sky-400 focus:outline-none px-1" />
        <div className="ml-auto flex items-center gap-2">
          <select value={unit} onChange={(e) => setStudy({ ...study, unit: e.target.value })} data-testid="etude-unit-select" className="px-2 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 bg-white">
            <option value="cm">Unité : cm</option>
            <option value="mm">Unité : mm</option>
          </select>
          <select value={markerCm} onChange={(e) => setStudy({ ...study, marker_cm: +e.target.value })} data-testid="marker-size-select" className="px-2 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 bg-white">
            <option value={10}>Marqueur 10 cm</option>
            <option value={15}>Marqueur 15 cm</option>
            <option value={20}>Marqueur 20 cm</option>
          </select>
          <button onClick={printMarker} data-testid="print-marker-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"><Printer className="w-4 h-4" /> Marqueur</button>
          <button onClick={toNesting} disabled={!img} data-testid="to-nesting-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40"><Scissors className="w-4 h-4" /> Imposition</button>
          <button onClick={openAttach} disabled={!img} data-testid="attach-devis-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40"><Paperclip className="w-4 h-4" /> Vers un devis</button>
          <button onClick={genQuote} disabled={genBusy || !study} data-testid="generate-quote-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-sky-400 text-navy text-sm font-semibold hover:bg-sky-500 disabled:opacity-50">{genBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Devis IA</button>
          <button onClick={save} disabled={saving} data-testid="etude-save-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy-deep disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer</button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <input value={study.brand || ""} onChange={(e) => setStudy({ ...study, brand: e.target.value })} placeholder="Marque" data-testid="etude-brand" className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
        <input value={study.model || ""} onChange={(e) => setStudy({ ...study, model: e.target.value })} placeholder="Modèle" data-testid="etude-model" className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
        <input value={study.client_name || ""} onChange={(e) => setStudy({ ...study, client_name: e.target.value })} placeholder="Client" data-testid="etude-client" className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
      </div>

      <div className="grid lg:grid-cols-[1fr,300px] gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-3">
          {/* toolbar */}
          <div className="flex items-center gap-1.5 flex-wrap mb-3">
            {TOOLS.map((t) => (
              <button key={t.key} onClick={() => setTool(t.key)} title={t.label} data-testid={`tool-${t.key}`}
                className={`p-2 rounded-lg transition-colors ${tool === t.key ? "bg-navy text-white" : "text-slate-500 hover:bg-slate-100"}`}><t.icon className="w-4 h-4" /></button>
            ))}
            <button onClick={autoCalibrate} title="Détecter le marqueur ArUco" data-testid="auto-calibrate-btn" className="p-2 rounded-lg text-violet-600 hover:bg-violet-50 inline-flex items-center gap-1"><Wand2 className="w-4 h-4" /><span className="text-[11px] font-semibold">Auto</span></button>
            <div className="w-px h-6 bg-slate-200 mx-1" />
            {COLORS.map((c) => (
              <button key={c} onClick={() => setColor(c)} data-testid={`color-${c}`} style={{ background: c }}
                className={`w-6 h-6 rounded-full border-2 ${color === c ? "border-sky-400 scale-110" : "border-white"} shadow transition-transform`} />
            ))}
            <div className="w-px h-6 bg-slate-200 mx-1" />
            <button onClick={deleteSelected} disabled={!selectedId} data-testid="delete-shape-btn" className="p-2 rounded-lg text-rose-500 hover:bg-rose-50 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
            <span className="text-[11px] text-slate-400 ml-2">{calibration?.pxPerCm ? `Calibré${calibration.homography ? ` (perspective${calibration.markers > 1 ? `, ${calibration.markers} marqueurs` : ""})` : ""} — échelle calibrée` : "Non calibré — cliquez « Auto » ou l'outil Calibrer"}</span>
          </div>

          {img ? (
            <div className="overflow-auto rounded-xl border border-slate-100 bg-slate-50" style={{ maxWidth: "100%" }} data-testid="etude-stage-wrap">
              <Stage ref={stageRef} width={displayW} height={displayH}
                onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp}
                onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
                style={{ cursor: tool === "select" ? "default" : "crosshair" }} data-testid="etude-stage">
                <Layer>
                  <KImage image={img} width={displayW} height={displayH} listening={false} />
                  {[...shapes, ...(draft ? [draft] : [])].map((s) => {
                    if (s.type === "pen") return <Line key={s.id} points={s.points} stroke={s.color} strokeWidth={strokeFor(s)} lineCap="round" lineJoin="round" tension={0.3} onClick={selectHandler(s.id)} onTap={selectHandler(s.id)} />;
                    if (s.type === "rect") return <Rect key={s.id} x={Math.min(s.x1, s.x2)} y={Math.min(s.y1, s.y2)} width={Math.abs(s.x2 - s.x1)} height={Math.abs(s.y2 - s.y1)} stroke={s.color} strokeWidth={strokeFor(s)} onClick={selectHandler(s.id)} onTap={selectHandler(s.id)} />;
                    if (s.type === "circle") return <Circle key={s.id} x={s.x1} y={s.y1} radius={dist(s.x1, s.y1, s.x2, s.y2)} stroke={s.color} strokeWidth={strokeFor(s)} onClick={selectHandler(s.id)} onTap={selectHandler(s.id)} />;
                    if (s.type === "arrow") return <Arrow key={s.id} points={[s.x1, s.y1, s.x2, s.y2]} stroke={s.color} fill={s.color} strokeWidth={strokeFor(s)} pointerLength={10} pointerWidth={10} onClick={selectHandler(s.id)} onTap={selectHandler(s.id)} />;
                    if (s.type === "text") return <Text key={s.id} x={s.x} y={s.y} text={s.text} fontSize={16} fontStyle="bold" fill={s.color} onClick={selectHandler(s.id)} onTap={selectHandler(s.id)} />;
                    if (s.type === "dimension" || s.type === "calibrate") {
                      const mx = (s.x1 + s.x2) / 2, my = (s.y1 + s.y2) / 2;
                      const lbl = s.type === "calibrate" ? "réf." : measureLabel(s);
                      const stroke = s.type === "calibrate" ? "#a855f7" : s.color;
                      return (
                        <Group key={s.id} onClick={selectHandler(s.id)} onTap={selectHandler(s.id)}>
                          <Line points={[s.x1, s.y1, s.x2, s.y2]} stroke={stroke} strokeWidth={strokeFor(s)} dash={s.type === "calibrate" ? [6, 4] : undefined} />
                          <Line points={[s.x1, s.y1 - 6, s.x1, s.y1 + 6]} stroke={stroke} strokeWidth={2} />
                          <Line points={[s.x2, s.y2 - 6, s.x2, s.y2 + 6]} stroke={stroke} strokeWidth={2} />
                          <Label x={mx} y={my - 10}>
                            <Tag fill="#ffffff" stroke={stroke} cornerRadius={4} />
                            <Text text={lbl} fontSize={13} fontStyle="bold" fill={stroke} padding={3} />
                          </Label>
                        </Group>
                      );
                    }
                    return null;
                  })}
                </Layer>
              </Stage>
            </div>
          ) : (
            <label onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addPhoto(e.dataTransfer.files); }} data-testid="etude-drop"
              className="flex flex-col items-center justify-center h-72 rounded-xl border-2 border-dashed border-slate-200 hover:border-sky-400 cursor-pointer text-slate-400 bg-slate-50">
              {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <><ImagePlus className="w-8 h-8 mb-2" /><span className="text-sm">Glissez une photo ou cliquez pour en importer une</span></>}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="etude-file" onChange={(e) => addPhoto(e.target.files)} />
            </label>
          )}

          {/* thumbnails */}
          {study.photos?.length > 0 && (
            <div className="flex gap-2 mt-3 flex-wrap items-center">
              {study.photos.map((p, i) => (
                <button key={i} onClick={() => { setActive(i); setSelectedId(null); }} data-testid={`photo-thumb-${i}`}
                  className={`w-14 h-14 rounded-lg overflow-hidden border-2 ${i === active ? "border-sky-400" : "border-transparent"}`}>
                  <img src={srcWithAuth(p.url)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
              <button onClick={() => fileRef.current?.click()} data-testid="add-photo-btn" className="w-14 h-14 rounded-lg border-2 border-dashed border-slate-200 text-slate-400 hover:border-sky-400 flex items-center justify-center">{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}</button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => addPhoto(e.target.files)} />
            </div>
          )}
        </div>

        {/* voice note panel */}
        <div className="space-y-3">
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-navy flex items-center gap-1.5 mb-2"><Mic className="w-4 h-4 text-sky-500" /> Note vocale (archive interne)</h3>
            <button onClick={toggleRecord} disabled={voiceBusy} data-testid="voice-record-btn"
              className={`w-full py-2.5 rounded-xl text-sm font-semibold inline-flex items-center justify-center gap-2 ${recording ? "bg-rose-500 text-white animate-pulse" : "bg-sky-400 text-navy hover:bg-sky-500"} disabled:opacity-50`}>
              {voiceBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Traitement IA…</> : recording ? <>■ Arrêter l'enregistrement</> : <><Mic className="w-4 h-4" /> Enregistrer une note</>}
            </button>
            {study.voice_url && (
              <audio controls src={srcWithAuth(study.voice_url)} className="w-full mt-3" data-testid="voice-audio" />
            )}
            {study.transcript && (
              <div className="mt-3">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Transcription</p>
                <p className="text-xs text-slate-600 mt-1 max-h-24 overflow-auto" data-testid="voice-transcript">{study.transcript}</p>
              </div>
            )}
            {study.ai_summary && (
              <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-100 p-3">
                <p className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider flex items-center gap-1"><Sparkles className="w-3 h-3" /> Résumé IA</p>
                <div className="text-xs text-slate-700 mt-1 whitespace-pre-wrap" data-testid="voice-summary">{study.ai_summary}</div>
              </div>
            )}
            {(study.transcript || study.ai_summary) && (
              <button onClick={genQuote} disabled={genBusy} data-testid="dictation-to-quote-btn"
                className="w-full mt-3 py-2 rounded-xl bg-navy text-white text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-navy-deep disabled:opacity-50">
                {genBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Générer un devis depuis la dictée
              </button>
            )}
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-navy flex items-center gap-1.5 mb-2"><Ruler className="w-4 h-4 text-sky-500" /> Grille de mesures <span className="ml-auto text-xs font-normal text-slate-400">{measures.length}</span></h3>
            {measures.length === 0 ? (
              <p className="text-xs text-slate-400">Tracez des cotes ou des rectangles (après calibration) pour les retrouver ici.</p>
            ) : (
              <table className="w-full text-xs" data-testid="measures-grid">
                <thead><tr className="text-slate-400 text-left"><th className="font-medium py-1">Photo</th><th className="font-medium">Type</th><th className="font-medium text-right">Valeur</th></tr></thead>
                <tbody>
                  {measures.map((m, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`measure-row-${i}`}>
                      <td className="py-1 text-slate-500">#{m.photo}</td>
                      <td className="text-slate-600">{m.type}</td>
                      <td className="text-right font-mono font-semibold text-navy">{m.val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-navy flex items-center gap-1.5 mb-2"><FileText className="w-4 h-4 text-sky-500" /> Devis générés <span className="ml-auto text-xs font-normal text-slate-400">{quotes.length}</span></h3>
            {quotes.length === 0 ? (
              <p className="text-xs text-slate-400">Aucun devis généré depuis cette étude pour l'instant.</p>
            ) : (
              <div className="space-y-1.5" data-testid="quote-history">
                {quotes.map((q) => (
                  <button key={q.id} onClick={() => navigate(`/documents/${q.id}`)} data-testid={`quote-history-${q.id}`}
                    className="w-full text-left px-3 py-2 rounded-xl border border-slate-200 hover:border-sky-400 hover:bg-sky-50/40 flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-navy truncate">{q.number || "Devis"}<span className="text-slate-400 font-normal"> · {q.client_name || "—"}</span></span>
                    <span className="text-xs font-mono text-slate-600 whitespace-nowrap">{(q.total_ttc || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-4 text-xs text-slate-500 leading-relaxed">
            <p className="font-semibold text-navy mb-1">Comment mesurer ?</p>
            1. Imprimez le marqueur (bouton « Marqueur ») et collez-le à plat sur la surface.<br />
            2. Prenez la photo bien de face et importez-la.<br />
            3. Outil <b>Calibrer</b> : tracez un côté du carré, saisissez 20 cm.<br />
            4. Outil <b>Cote</b> : vos mesures s'affichent alors en cm.
          </div>
        </div>
      </div>

      {attachOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setAttachOpen(false)} data-testid="attach-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-display font-bold text-navy mb-3">Ajouter le plan aux annexes d'un devis</h2>
            <div className="space-y-1.5 max-h-80 overflow-auto">
              {docs.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Aucun devis trouvé.</p>}
              {docs.map((d) => (
                <button key={d.id} onClick={() => attachToDoc(d)} data-testid={`attach-doc-${d.id}`}
                  className="w-full text-left px-3 py-2.5 rounded-xl border border-slate-200 hover:border-sky-400 hover:bg-sky-50/40 flex items-center justify-between">
                  <span className="text-sm font-semibold text-navy">{d.number || "Devis"} — {d.client_name || "Sans client"}</span>
                  <span className="text-xs text-slate-400">{new Date(d.created_at || Date.now()).toLocaleDateString("fr-FR")}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
